import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography, FormControlLabel, Switch } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { createTransitionApi, updateTransitionApi } from '../apiService';
import { ALLOWED_ROLE_OPTIONS } from '../constants';

const errorMessage = (response) => response?.data?.message || 'Unable to save the transition due to a technical error.';

const OptionField = ({ label, options, value, error, onChange, onFocus, disabled = false, required = true }) => (
  <Autocomplete
    options={options || []}
    disabled={disabled}
    getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name || option?.label)) || ''}
    getOptionSelected={(option, current) => (typeof option === 'string' ? option === current : option?.id === current?.id)}
    value={value || null}
    onChange={(event, newValue) => onChange(newValue || null)}
    renderInput={(params) => (
      <TextField
        {...params}
        required={required}
        label={label}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        error={!!error}
        helperText={error}
        onFocus={onFocus}
      />
    )}
  />
);

const TransitionFormDialog = ({ stages, transitions, addEditTransition, onToggleModal, getResponseBack, enqueueSnackbar }) => {
  const isEdit = addEditTransition?.mode === 'edit';
  const transition = addEditTransition?.data;
  const [data, setData] = useState({
    fromStage: isEdit ? transition?.from_stage : null,
    toStage: isEdit ? transition?.to_stage : null,
    label: isEdit ? transition?.label : '',
    allowedRoles: isEdit ? (transition?.allowed_roles || []) : [],
    requiresConfirmation: isEdit ? !!transition?.requires_confirmation : false,
    confirmMessage: isEdit ? (transition?.confirm_message || '') : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({});

  const updateData = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const clearError = (key) => setError((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));

  const handleFromStageChange = (value) => {
    updateData({ fromStage: value, toStage: null });
  };

  const isSamePair = data.fromStage && data.toStage && data.fromStage?.id === data.toStage?.id;
  const isDuplicatePair = data.fromStage && data.toStage && !isSamePair && (transitions || []).some(
    (t) => t?.id !== transition?.id && t?.from_stage?.id === data.fromStage?.id && t?.to_stage?.id === data.toStage?.id,
  );

  const handleSave = () => {
    const validationInputs = {
      fromStage: data.fromStage ? '' : 'Please select From Stage',
      toStage: !data.toStage ? 'Please select To Stage' : isSamePair ? 'From Stage and To Stage cannot be the same' : isDuplicatePair ? 'A transition for this From/To pair already exists' : '',
    };
    if (!Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
      setError(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Transition Failed',
        page_title: 'Planner Tracker Add/Update Transition',
        transition_id: transition?.id || '',
        from_stage_id: data.fromStage?.id || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
      return;
    }

    setSaving(true);
    const payload = {
      to_stage: data.toStage?.id,
      label: data.label?.trim(),
      allowed_roles: data.allowedRoles,
      requires_confirmation: data.requiresConfirmation,
      ...(data.requiresConfirmation ? { confirm_message: data.confirmMessage?.trim() } : {}),
    };
    const request = isEdit
      ? globalPutService(updateTransitionApi(transition?.id), payload)
      : globalPostService(createTransitionApi(data.fromStage?.id), payload);
    request.then((response) => {
      setSaving(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(isEdit ? 'Transition updated' : 'Transition created', { variant: 'success' });
        getResponseBack();
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Success`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition`,
          transition_id: transition?.id || '',
          from_stage_id: data.fromStage?.id || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Failed`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition`,
          transition_id: transition?.id || '',
          from_stage_id: data.fromStage?.id || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <Dialog open={!!addEditTransition?.modal} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{isEdit ? 'Edit Transition' : 'Add Transition'}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <OptionField
              label="From Stage"
              options={stages}
              value={data.fromStage}
              onChange={handleFromStageChange}
              disabled={isEdit}
              error={error.fromStage}
              onFocus={() => clearError('fromStage')}
            />
          </Grid>
          <Grid item xs={6}>
            <OptionField
              label="To Stage"
              options={(stages || []).filter((s) => !data.fromStage || s?.id !== data.fromStage?.id)}
              value={data.toStage}
              onChange={(value) => updateData({ toStage: value })}
              disabled={!data.fromStage || isEdit}
              error={error.toStage}
              onFocus={() => clearError('toStage')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth label="Label" variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              value={data.label} onChange={(event) => updateData({ label: event.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={ALLOWED_ROLE_OPTIONS}
              getOptionLabel={(option) => option}
              value={data.allowedRoles}
              onChange={(event, newValue) => updateData({ allowedRoles: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Allowed Roles" variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={data.requiresConfirmation}
                  onChange={(event) => updateData({ requiresConfirmation: event.target.checked })}
                  color="primary"
                />
              )}
              label="Requires Confirmation"
            />
          </Grid>
          {data.requiresConfirmation ? (
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={2} label="Confirmation Message" variant="outlined" size="small"
                InputLabelProps={{ shrink: true }}
                value={data.confirmMessage} onChange={(event) => updateData({ confirmMessage: event.target.value })}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={() => onToggleModal()}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Transition'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransitionFormDialog;
